/**
 * Session list hook for fetching and managing sessions
 * Follows ADR-003: High Cohesion - Single responsibility: session data management
 */

import { useState, useCallback, useEffect } from 'react';
import { log, logWarn, logError } from '../utils/logger';
import { SessionService } from '../services/SessionService';

/**
 * Session data structure
 */
export interface Session {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  customer_id?: string;
  agent_id: string;
}

/**
 * Options for useSessionList hook
 */
export interface UseSessionListOptions {
  /** Server URL */
  serverUrl: string;
  /** Authentication token */
  authToken?: string;
  /** Agent ID */
  agentId: string;
  /** Customer ID */
  customerId: string;
}

/**
 * Return value for useSessionList hook
 */
export interface UseSessionListReturn {
  /** List of sessions */
  sessions: Session[];
  /** Whether sessions are loading */
  loading: boolean;
  /** Error message if any */
  error: string;
  /** Whether a refresh is in progress */
  isRefreshing: boolean;
  /** Whether there are more pages to load */
  hasMore: boolean;
  /** Fetch/refresh sessions from the first page */
  fetchSessions: () => Promise<void>;
  /** Load the next page of sessions */
  fetchNextPage: () => Promise<void>;
  /** Delete a session */
  deleteSession: (sessionId: string) => Promise<void>;
  /** Clear error */
  clearError: () => void;
}

/**
 * Parses a date string robustly handling multiple formats
 */
function parseDate(dateString: string): Date {
  if (!dateString) return new Date(0);

  if (dateString.includes('/')) {
    const parts = dateString.split(' ');
    if (parts.length >= 2) {
      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split('/');
      const [hour, minute, second] = timePart.split(':');
      const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${(second || '00').padStart(2, '0')}`;
      return new Date(isoString);
    }
  } else if (dateString.includes('.')) {
    const parts = dateString.split(' ');
    if (parts.length >= 2) {
      const [datePart, timePart] = parts;
      const [day, month, year] = datePart.split('.');
      const [hour, minute, second] = timePart.split('.');
      const isoString = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hour.padStart(2, '0')}:${minute.padStart(2, '0')}:${(second || '00').padStart(2, '0')}`;
      return new Date(isoString);
    }
  }

  return new Date(dateString);
}

/**
 * Sorts sessions by date descending (most recent first)
 * Future dates are capped to current time to prevent data errors from affecting sort order
 */
function sortSessionsByDate(sessions: Session[]): Session[] {
  const now = Date.now();
  
  return [...sessions].sort((a, b) => {
    let aDateString = a.updated_at || a.created_at;
    let bDateString = b.updated_at || b.created_at;

    if (!aDateString && a.title?.startsWith('Chat Session ')) {
      const match = a.title.match(/Chat Session (.+)/);
      if (match) aDateString = match[1];
    }

    if (!bDateString && b.title?.startsWith('Chat Session ')) {
      const match = b.title.match(/Chat Session (.+)/);
      if (match) bDateString = match[1];
    }

    let aDate = parseDate(aDateString || '').getTime();
    let bDate = parseDate(bDateString || '').getTime();

    // Cap future dates to current time to prevent incorrect sorting
    // (handles data errors where dates are mistakenly set in the future)
    if (aDate > now) aDate = now;
    if (bDate > now) bDate = now;

    return bDate - aDate;
  });
}

/**
 * Custom hook for managing session list data
 */
export function useSessionList({
  serverUrl,
  authToken,
  agentId,
  customerId
}: UseSessionListOptions): UseSessionListReturn {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  const cleanServerUrl = serverUrl.endsWith('/') ? serverUrl.slice(0, -1) : serverUrl;
  const service = new SessionService(cleanServerUrl, authToken);

  /**
   * Fetches first page of sessions from the server
   */
  const fetchSessions = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

      log('SessionList: Fetching sessions for agentId:', agentId, 'customerId:', customerId);

      if (!agentId?.trim() || !customerId?.trim()) {
        logWarn('SessionList: agentId or customerId is empty');
        setLoading(false);
        return;
      }

      const page = await service.fetchSessionsPage(customerId.trim(), { limit: 50 });
      log('SessionList: Fetched sessions:', page.items.length, 'nextCursor:', page.nextCursor);

      const sortedSessions = sortSessionsByDate(page.items);
      setSessions(sortedSessions);
      setNextCursor(page.nextCursor);
    } catch (err) {
      logError('Error fetching sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load sessions');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanServerUrl, authToken, agentId, customerId]);

  /**
   * Loads the next page and appends to the existing list
   */
  const fetchNextPage = useCallback(async (): Promise<void> => {
    if (!nextCursor) return;
    try {
      const page = await service.fetchSessionsPage(customerId.trim(), { cursor: nextCursor, limit: 50 });
      setSessions(prev => sortSessionsByDate([...prev, ...page.items]));
      setNextCursor(page.nextCursor);
    } catch (err) {
      logError('Error fetching next page of sessions:', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextCursor, cleanServerUrl, authToken, customerId]);

  /**
   * Deletes a session
   */
  const deleteSession = useCallback(async (sessionId: string): Promise<void> => {
    const headers: Record<string, string> = {};
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    const response = await fetch(`${cleanServerUrl}/sessions/${sessionId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      throw new Error(`Failed to delete session: ${response.status}`);
    }

    setSessions(prev => prev.filter(s => s.id !== sessionId));
  }, [cleanServerUrl, authToken]);

  /**
   * Clears error
   */
  const clearError = useCallback((): void => {
    setError('');
  }, []);

  // Fetch sessions on mount
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  return {
    sessions,
    loading,
    error,
    isRefreshing,
    hasMore: nextCursor !== null,
    fetchSessions,
    fetchNextPage,
    deleteSession,
    clearError
  };
}

